<template>
  <div class="mb-4 list-options mb-sm-2">
    <v-card border>
      <v-card-title class="toolbar-title">
        {{ $t('leads.page-title') }}
      </v-card-title>
      <v-card-text
        class="flex-wrap w-full d-flex pa-0"
        :class="{
          'justify-space-between': $vuetify.display.mdAndUp || $vuetify.display.xs,
          'justify-center': $vuetify.display.sm,
        }"
      >
        <div
          class="d-flex"
          :class="{ 'w-full justify-space-between flex-column': $vuetify.display.xs }"
        >
          <!-- Filter -->
          <div
            v-if="!isPlatformMarkero"
            class="d-flex align-center px-2 option-container"
            :class="{ 'w-full': $vuetify.display.xs }"
          >
            <v-badge
              :model-value="activeFiltersCount > 0"
              :content="activeFiltersCount"
              color="red"
              floating
              :class="{ 'w-full': $vuetify.display.xs }"
              class="notification-badge"
              location="right"
            >
              <v-btn
                variant="outlined"
                color="primary"
                class="camel-case option-btn"
                :href="'javascript:void(0)'"
                :to="undefined"
                type="button"
                @click="$emit('toggleLeadListTool', 'filter')"
              >
                <v-icon start> mdi-filter-variant </v-icon>
                {{ $t('labels.filter') }}
              </v-btn>
            </v-badge>
          </div>
          <!-- Search -->
          <div
            class="pa-2 flex-grow-1 lead-list__search"
            outlined
          >
            <!-- TODO: add outlined styles -->
            <!-- variant="solo" -->
            <v-text-field
              :model-value="listOptions.search"
              class="ml-md-2"
              :label="$t('labels.leads.lead-search')"
              hide-details
              lines="one"
              clearable
              variant="outlined"
              flat
              density="compact"
              append-inner-icon="mdi-magnify"
              @update:model-value="onSearchChange"
            />
          </div>
        </div>

        <!-- Expanded Actions for Desktop -->
        <div
          class="d-flex align-center"
          :class="{ 'w-full justify-space-between flex-column': $vuetify.display.xs }"
        >
          <!-- Create new Lead -->
          <Permission permission="lead:create">
            <CreateLeadDialog
              class="px-2"
              :refetch-parent="refetchParent"
            >
              <v-btn
                color="primary"
                class="camel-case w-full text-white"
              >
                <v-icon
                  size="small"
                  start
                  color="white"
                >
                  mdi-account-plus
                </v-icon>
                {{ $t('buttons.leads.create') }}
              </v-btn>
            </CreateLeadDialog>
          </Permission>

          <!-- Import -->
          <Permission permission="lead:import">
            <Feature :feature-slug="featureNames.LEAD_IMPORT">
              <v-btn
                v-if="$vuetify.display.mdAndUp"
                color="primary"
                class="camel-case mx-2"
                @click="$emit('showImport')"
              >
                <v-icon
                  size="small"
                  start
                >
                  mdi-upload
                </v-icon>
                {{ $t('buttons.leads.import') }}
              </v-btn>
            </Feature>
          </Permission>

          <!-- Acquisition Booster -->
          <Permission permission="lead:invite-bulk">
            <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
              <AcquisitionBoosterButton
                v-if="!isPipelineView"
                :is-disabled="mailProvider !== 'HOPPERMATION'"
                icon="mdi-rocket-launch-outline"
                :label="$t('labels.acquisition-booster.name')"
                @click="openAcquisitionBooster"
              />
            </Feature>
          </Permission>
        </div>

        <!-- List or Grid View -->
        <div
          class="d-flex"
          :class="{
            'align-center': $vuetify.display.smAndUp,
            'px-2': $vuetify.display.smAndDown,
          }"
        >
          <!-- List view -->
          <Permission permission="lead:write">
            <All
              :leads-view-selected="leadsViewSelected"
              @select="changeView('list')"
            />
          </Permission>

          <!-- Grid view -->
          <Permission permission="lead:write">
            <Platform
              :leads-view-selected="leadsViewSelected"
              @select="changeView('pipeline')"
            />
          </Permission>
        </div>

        <!-- Actions -->
        <div
          class="justify-end d-flex align-center actions"
          :class="{ 'justify-space-between flex-column': $vuetify.display.xs }"
        >
          <!-- More Functions -->
          <div
            class="px-2 option-container d-flex align-center"
            :class="{ 'w-full': $vuetify.display.xs }"
          >
            <!-- TODO: check unsupported prop: offset-y -->
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  class="camel-case vertical-dots-wrapper"
                  color="primary"
                  variant="outlined"
                  v-bind="props"
                >
                  <v-icon
                    class="vertical-dots"
                    start
                  >
                    mdi-dots-vertical
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <Permission permission="lead:export-bulk">
                  <v-list-item class="list-item-container">
                    <Feature :feature-slug="featureNames.LEAD_EXPORT">
                      <Export @exportLeads="(value) => $emit('exportLeads', value)" />
                    </Feature>
                  </v-list-item>
                </Permission>

                <v-list-item class="list-item-container">
                  <ColumnSettingsButton
                    icon="mdi-view-column"
                    :label="$t('labels.display')"
                    @click="$emit('toggleLeadListTool', 'column')"
                  />
                </v-list-item>

                <Permission
                  v-if="!isPipelineView"
                  permission="lead:delete-bulk"
                >
                  <Feature :feature-slug="featureNames.DELETE_LEADS">
                    <v-list-item class="list-item-container">
                      <ColumnSettingsButton
                        icon="mdi-delete"
                        :label="$t('labels.delete')"
                        @click="$emit('toggleLeadListTool', 'deleteLeads')"
                      />
                    </v-list-item>
                  </Feature>
                </Permission>

                <Permission
                  v-if="!isPipelineView"
                  permission="lead:assign-bulk"
                >
                  <v-list-item class="list-item-container">
                    <ColumnSettingsButton
                      icon="mdi-account-arrow-right-outline"
                      :label="$t('labels.assign')"
                      @click="$emit('toggleLeadListTool', 'assignLeads')"
                    />
                  </v-list-item>
                </Permission>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { inject } from 'vue'

import Permission from '@/components/Permission'
import { debounce } from '@/lib/debounce'
import { setLocalStorage } from '@/lib/localStorage'
import brandingMixin from '@/mixins/branding'
import currencyMixin from '@/mixins/currency'
import featureMixin from '@/mixins/feature'
import All from '@/modules/leads/All.vue'
import Platform from '@/modules/leads/Platform.vue'

import Export from '../Export.vue'
import MAIL_PROVIDER from '../queries/MailProvider.gql'
import AcquisitionBoosterButton from './AcquisitionBoosterButton.vue'
import ColumnSettingsButton from './ColumnSettingsButton.vue'
import CreateLeadDialog from './CreateLeadDialog.vue'

export default {
  components: {
    Platform,
    All,
    Export,
    AcquisitionBoosterButton,
    CreateLeadDialog,
    ColumnSettingsButton,
    Permission,
  },
  mixins: [currencyMixin, brandingMixin, featureMixin],
  props: {
    leadsViewSelected: {
      type: String,
      required: false,
    },
    listOptions: {
      type: Object,
      required: true,
    },
    activeFiltersCount: {
      type: Number,
      required: true,
    },
    refetchParent: {
      type: Function,
      required: true,
    },
  },
  setup() {
    const tracking = inject('tracking')

    const debounceSearchTracking = debounce(
      (value) => tracking.event('Leads', 'Selected', 'Search Leads', value),
      500
    )

    return { debounceSearchTracking }
  },
  computed: {
    isPipelineView() {
      return this.leadsViewSelected === 'pipeline'
    },
  },
  methods: {
    onClick(label, value) {
      this.$tracking.event('Leads', 'Clicked', label, value)
    },
    onSearchChange(value) {
      this.$emit('updateSearch', { ...this.listOptions, search: value })
      if (value) this.debounceSearchTracking(value)
    },
    changeView(view) {
      setLocalStorage('leadsViewSelected', view)
      this.$emit('changeView', view)
      this.$tracking.event('Leads', 'Clicked', 'View Changed', view)
    },
    openAcquisitionBooster() {
      this.$tracking.event('Leads', 'Clicked', 'Acquisition Booster')
      this.$emit('toggleLeadListTool', 'invitation')
    },
  },
  apollo: {
    mailProvider: {
      query: MAIL_PROVIDER,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
      update: (data) => data.company.mailProvider,
    },
  },
  emits: ['changeView', 'exportLeads', 'toggleLeadListTool', 'showImport', 'updateSearch'],
}
</script>

<style>
:deep(.v-field--variant-outlined .v-field__outline:not(.v-field--error .v-field__outline)) {
  color: rgba(0, 0, 0, 0.38);
}

.v-field__outline {
  color: rgba(0, 0, 0, 0.38);
  opacity: 1;
}

.toolbar-title {
  color: #414142;
  padding: 5px 10px 0;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
}

.option-container {
  height: 50px;
}

.option-btn {
  width: 100%;
}

.option-btn .v-btn__content {
  padding-left: 10px;
  padding-right: 10px;
}

/* breakpoint md and up */
@media (min-width: 600px) {
  .lead-list__search {
    min-width: 350px;
    max-width: 350px;
  }
}

.camel-case {
  text-transform: unset !important;
  font-weight: normal;
}

.vertical-dots-wrapper {
  min-width: unset !important;
}

.vertical-dots {
  margin: 0 !important;
}

.list-options .v-input--selection-controls .v-input__slot,
.v-input--select {
  margin-bottom: 0;
}

.list-options .v-text-field.v-text-field--enclosed {
  margin-top: 0px !important;
}

.activate-window-step {
  max-width: 600px;
}

.list-item-container {
  padding: 0px;
}

.list-item-container:hover {
  background-color: rgb(245, 245, 245);
}

.menu-list-item {
  padding-left: 15px;
  padding-right: 15px;
  height: 48px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.filter-container {
  position: relative;
}

.notification-badge .v-badge__badge {
  top: 4px !important;
  left: calc(100% - 12px) !important;
}

@media (min-width: 644px) and (max-width: 757px), (min-width: 1325px) {
  .actions .option-container {
    height: 50px;
  }
}
</style>
